import * as React from 'react'
import { moment, momentMergeRanges } from '../../../../../services/utils/moment.service'
import { utilRange } from '../../../../../services/utils/utils.service'
import { I18nService } from '../../../../../services/utils/i18n.service'
import { styled } from 'styled-components'
import { useMediaQuery } from '../../../../../hooks/use-media-query'
import { theme } from '../../../../../theme'
import CheckboxComponent from '../../../../shared/checkbox.component'

interface Props {
  name: string

  /** Availabilities should be well sorted in asc order */
  appointmentAvailabilities: { start_date: string, duration: number }[]
  
  /**
   * Empty array means any date isallowed when enableAnytimeMode = true.
   * Fulled array means only theses ranges are allowed.
   * Undefined means no date is allowed (no appointment proposal being created).
   */
  selectedRanges: { start: string, end: string }[] | undefined
  
  /**
   * If true, empty `selectedRanges` means any date is valid.
   * If false, empty `selectedRanges` means ranges need to be selected.
   */
  enableAnytimeMode: boolean

  onChange: (ranges: Props['selectedRanges']) => void
}

const CustomInputRadioLabel = styled.label<{ $isBeingSelected: boolean, $checked: boolean }>`
  height: 100%;

  &::before {
    background-color: ${ props => props.$checked ? props.theme.colors.clr_1 : props.theme.colors.clr_0_light } !important;
    box-shadow: ${ props => props.$isBeingSelected ? `0 0 0 0.25em ${props.$checked ? props.theme.colors.clr_1_light : props.theme.colors.clr_0_lighten_altenate}` : 'none' } !important;
  }
`

const i18nScoped = new I18nService(require('./casting-answer-employees-appointments-suggested-ranges-picker.component.yml'))

export default function CastingAnswerEmployeesAppointmentsSuggestedRangesPickerComponent ({ name, enableAnytimeMode, appointmentAvailabilities: availabilities, selectedRanges, onChange }: Props) {
  const isAllowingAnyTime = enableAnytimeMode && selectedRanges?.length === 0
  const selectedRangesMoment = React.useMemo(() => selectedRanges?.map(range => moment.range(moment(range.start), moment(range.end))), [selectedRanges])
  const availabilitiesRanges = React.useMemo(() => availabilities.map(availability => moment.range(
    moment(availability.start_date),
    moment(availability.start_date).add(availability.duration, 'seconds')
  )), [availabilities])

  const [currentStartDay, setCurrentStartDay] = React.useState<moment.Moment>((availabilitiesRanges[0]?.start.clone() || moment()).startOf('week'))
  const isSmallScreen = useMediaQuery(`(max-width: ${ theme.breakpoints.m }px)`)
  const numberOfDaysToShow = React.useMemo(() => isSmallScreen ? 4 : 7, [isSmallScreen])

  const [minHour, maxHour] = React.useMemo(() => 
    availabilitiesRanges.some(range => range.diff('hours') >= 24)
      ? [0, 24]
      : availabilitiesRanges.reduce(([min, max], range) => [Math.min(min, range.start.hour()), Math.max(max, range.end.hour())], [24, 0]),
    [availabilitiesRanges]
  )

  const isHourAvailable = React.useCallback((day: number, hour: number) => {
    const start = currentStartDay.clone().add(day, 'days').hour(hour).minute(0).second(0)
    const end = start.clone().add(1, 'hours')
    const hourRange = moment.range(start, end)
    return availabilitiesRanges.some(range => range.overlaps(hourRange))
  }, [availabilitiesRanges, currentStartDay])

  const isHourSelected = React.useCallback((day: number, hour: number) => {
    const start = currentStartDay.clone().add(day, 'days').hour(hour).minute(0).second(0)
    const end = start.clone().add(1, 'hours')
    const hourRange = moment.range(start, end)
    return !!selectedRangesMoment?.some(range => range.overlaps(hourRange))
  }, [currentStartDay, selectedRangesMoment])

  const onToggleHour = React.useCallback((day: number, hour: number) => {
    const start = currentStartDay.clone().add(day, 'days').hour(hour).minute(0).second(0)
    const end = start.clone().add(1, 'hours')
    const hourRange = moment.range(start, end)
    let newSelectedRanges = selectedRangesMoment?.slice() || []

    if (isHourSelected(day, hour)) {
      newSelectedRanges = newSelectedRanges.flatMap(_ => _.subtract(hourRange)).filter(_ => !!_)
    } else {
      newSelectedRanges.push(hourRange)
      newSelectedRanges = momentMergeRanges(newSelectedRanges)
    }

    const stringRanges = newSelectedRanges.map(_ => ({ start: _.start.format(), end: _.end.format() }))
    onChange(stringRanges.length > 0 ? stringRanges : undefined)
  }, [currentStartDay, isHourSelected, onChange, selectedRangesMoment])
  
  // Various state to handle the current selection range (first point touched, last point touched, touch in progress, etc.)
  // Start and end refers to the first and last point touched, not the datetime ordering (ongoingSelectionStart can be after ongoingSelectionEnd).
  // Warning: the `day` part is relative to the currentStartDay, not the actual day of the week or of the month.
  const [ongoingSelectionStart, setOngoingSelectionStart] = React.useState<Record<'day' | 'hour', number> | undefined>()
  const [ongoingSelectionEnd, setOngoingSelectionEnd] = React.useState<Record<'day' | 'hour', number> | undefined>()
  const [isSelectionStarted, setIsSelectionStarted] = React.useState(false)
  const containerRef = React.useRef<HTMLTableElement>()

  // Build regular moment dates from the selection range, allowing easier datetime comparisons, etc... and reordering of range bounds if needed.
  const momentOngoingSelectionStart = React.useMemo(() => ongoingSelectionStart && currentStartDay.clone().add(ongoingSelectionStart.day, 'day').hour(ongoingSelectionStart.hour).minute(0).second(0), [currentStartDay, ongoingSelectionStart])
  const momentOngoingSelectionEnd = React.useMemo(() => ongoingSelectionEnd && currentStartDay.clone().add(ongoingSelectionEnd.day, 'day').hour(ongoingSelectionEnd.hour).minute(0).second(0), [currentStartDay, ongoingSelectionEnd])
  const lowerSelectionBound = React.useMemo(() => momentOngoingSelectionStart && momentOngoingSelectionEnd && moment.min(momentOngoingSelectionStart, momentOngoingSelectionEnd), [momentOngoingSelectionEnd, momentOngoingSelectionStart])
  const upperSelectionBound = React.useMemo(() => momentOngoingSelectionStart && momentOngoingSelectionEnd && moment.max(momentOngoingSelectionStart, momentOngoingSelectionEnd), [momentOngoingSelectionEnd, momentOngoingSelectionStart])

  /**
   * Returns `true` if the given position in the timegrid is in the ongoing selection range.
   * Warning: returning `true` does not mean the hour will be selected after the selection is committed,
   * just that it is in the selection range, meaning its state will be aligned with the first point.
   */
  const isOngoingSelectedHour = React.useCallback((dayChoice: number, hourChoice: number) => {
    if (!ongoingSelectionStart || !ongoingSelectionEnd) return false
    const momentTestedSlot = currentStartDay.clone().add(dayChoice, 'day').hour(hourChoice).minute(0).second(0)
    return momentTestedSlot.isBetween(lowerSelectionBound.clone().subtract(1, 'second'), upperSelectionBound.clone().add(1, 'second'))
  }, [ongoingSelectionStart, ongoingSelectionEnd, currentStartDay, lowerSelectionBound, upperSelectionBound])

  /**
   * Returns the value of the selection range once committed.
   * It fully depends on the fst point of the selection range.
   * The whole range will be selected if the fst point is not selected, and unselected if it is.
   */
  const newSelectedValue = React.useMemo(() => {
    return ongoingSelectionStart ? !isHourSelected(ongoingSelectionStart.day, ongoingSelectionStart.hour) : false
  }, [isHourSelected, ongoingSelectionStart])

  const handleMultiSelectionStart = React.useCallback(() => setIsSelectionStarted(true), [])

  const handleMultiSelectionEnd = React.useCallback(() => {
    setIsSelectionStarted(false)
  
    // Commit selection
    if (ongoingSelectionStart && lowerSelectionBound && upperSelectionBound) {
      const swippedRange = moment.range(lowerSelectionBound, upperSelectionBound)
      const hourDaysToChange = Array.from(swippedRange.by('hour')).map(momentHourChoice => {
        const diffDay = momentHourChoice.diff(currentStartDay, 'day')
        const hourChoice = momentHourChoice.get('hour')
        return { diffDay, hourChoice }
      }).filter(({ diffDay, hourChoice }) => isHourAvailable(diffDay, hourChoice))

      if (!hourDaysToChange.length) return

      let newSelectedRanges = selectedRangesMoment?.slice() || []

      for (const { diffDay, hourChoice } of hourDaysToChange) {
        const start = currentStartDay.clone().add(diffDay, 'days').hour(hourChoice).minute(0).second(0)
        const end = start.clone().add(1, 'hours')
        const hourRange = moment.range(start, end)

        if (isHourSelected(diffDay, hourChoice) !== newSelectedValue) {
          if (isHourSelected(diffDay, hourChoice)) {
            newSelectedRanges = newSelectedRanges.flatMap(_ => _.subtract(hourRange)).filter(_ => !!_)
          } else {
            newSelectedRanges.push(hourRange)
            newSelectedRanges = momentMergeRanges(newSelectedRanges)
          }
        }
      }

      const stringRanges = newSelectedRanges.map(_ => ({ start: _.start.format(), end: _.end.format() }))
      onChange(stringRanges.length > 0 ? stringRanges : undefined)
    }

    // reset selection
    setOngoingSelectionStart(undefined)
    setOngoingSelectionEnd(undefined)
  }, [currentStartDay, isHourAvailable, isHourSelected, lowerSelectionBound, newSelectedValue, onChange, ongoingSelectionStart, selectedRangesMoment, upperSelectionBound])

  const handleMultiSelectionChange = React.useCallback((event: MouseEvent | TouchEvent) => {
    event.preventDefault()
    const target = ('touches' in event ? document.elementFromPoint(event.touches[0].clientX, event.touches[0].clientY) : event.target) as HTMLTableCellElement | HTMLLabelElement
    const { day, hour } = target.dataset 
    if (!isSelectionStarted || !day || !hour) return

    if (!ongoingSelectionStart) setOngoingSelectionStart({ day: +day, hour: +hour })
    else setOngoingSelectionEnd({ day: +day, hour: +hour })
  }, [isSelectionStarted, ongoingSelectionStart])

  React.useEffect(() => {
    if (isAllowingAnyTime) return undefined
    const containerRefElement = containerRef.current
    const eventListeners = [
      ['mousedown', handleMultiSelectionStart],
      ['mouseup', handleMultiSelectionEnd],
      ['mousemove', handleMultiSelectionChange],
      ['touchstart', handleMultiSelectionStart],
      ['touchend', handleMultiSelectionEnd],
      ['touchmove', handleMultiSelectionChange]
    ] as const

    eventListeners.forEach(([type, listener]) => containerRefElement.addEventListener(type, listener))
    return () => eventListeners.forEach(([type, listener]) => containerRefElement.removeEventListener(type, listener))
  }, [handleMultiSelectionEnd, handleMultiSelectionStart, handleMultiSelectionChange, isAllowingAnyTime])

  const goToPreviousWeek = React.useCallback(() => setCurrentStartDay(currentStartDay.clone().subtract(numberOfDaysToShow, 'day')), [currentStartDay, numberOfDaysToShow])
  const goToNextWeek = React.useCallback(() => setCurrentStartDay(currentStartDay.clone().add(numberOfDaysToShow, 'day')), [currentStartDay, numberOfDaysToShow])
  const canGoPrevious = React.useMemo(() => {
    return currentStartDay.clone().subtract(numberOfDaysToShow ,'day').isAfter(availabilitiesRanges[0]?.start.clone().startOf('day').subtract(numberOfDaysToShow ,'day'))
  }, [availabilitiesRanges, currentStartDay, numberOfDaysToShow])
  const canGoNext = React.useMemo(() =>{
    return currentStartDay.clone().add(numberOfDaysToShow, 'day').isBefore(availabilitiesRanges[availabilitiesRanges.length - 1]?.end.clone().startOf('day').add(numberOfDaysToShow, 'day'))
  }, [availabilitiesRanges, currentStartDay, numberOfDaysToShow])

  return <React.Fragment>
    { enableAnytimeMode && <CheckboxComponent
      id="appointment-proposal-any-time"
      checked={ isAllowingAnyTime }
      label={ i18nScoped.t(`no_date_or_time_preference_${window.currentUser.type.toLocaleLowerCase()}`) }
      onChange={ () => onChange(isAllowingAnyTime ? undefined : []) }
      className="u-mbm" /> }

    { !isAllowingAnyTime && <div>
      <div className="selectableTable selectableTable--noLabel">
        <table ref={ containerRef } style={{ userSelect: 'none' }}>
          <thead>
            <tr>
              <th className="u-txtLeft">
                { canGoPrevious && <a className="icon-prev" style={{ fontWeight: 'bold' }} onClick={ goToPreviousWeek }></a> }
              </th>

              { utilRange(0, numberOfDaysToShow, 1).map(day => <th key={ day }>{ currentStartDay.clone().add(day, 'days').format('dd DD') }</th>) }

              <th className="u-txtRight">
                { canGoNext && <a className="icon-next" style={{ fontWeight: 'bold' }} onClick={ goToNextWeek }></a> }
              </th>
            </tr>
          </thead>

          <tbody>
            { utilRange(minHour, maxHour, 1).map(hourChoice => <tr key={ hourChoice }>
              <td className="u-txtCenter"><b>{ hourChoice }h</b></td>
            
              { utilRange(0, numberOfDaysToShow, 1).map(day => <td key={ day } className='u-txtCenter'>
                { isHourAvailable(day, hourChoice) ? <div className="inputRadio inputRadio--full" 
                  style={{ height: '100%' }}>
                  <input
                    type="checkbox"
                    id={ `${name}-day-${day}-hour-${hourChoice}` }
                    checked={ isHourSelected(day, hourChoice) }
                    onChange={ () => onToggleHour(day, hourChoice) } />

                  <CustomInputRadioLabel 
                    htmlFor={ `${name}-day-${day}-hour-${hourChoice}` } 
                    $checked={ isSelectionStarted && isOngoingSelectedHour(day, hourChoice) ? newSelectedValue : isHourSelected(day, hourChoice) }
                    $isBeingSelected={ isSelectionStarted && isOngoingSelectedHour(day, hourChoice) }
                    data-day={ day }
                    data-hour={ hourChoice } />
                </div> : '-' }
              </td>) }

              <td></td>
            </tr>) }
          </tbody>
        </table>
      </div>

      <div className='txtSmall'>{ i18nScoped.t('press_and_hold') }</div>
    </div> }
  </React.Fragment>
}
